import styled from "styled-components";
import { colors } from "../../Shared/colors";

/**
 * Shared visual language for the four Staff screens — Manage Staff, Manage
 * Team, Team Hierarchy and Jagiir Contact.
 *
 * These screens were each built separately and drifted apart: different card
 * treatments, different table densities, different button shapes for the same
 * action. Putting the primitives here is what lets them be redesigned one at a
 * time without the section looking half-finished in between.
 *
 * The scale is the data-dense dashboard one already used by the Social Media
 * screen — 8px gaps, compact rows, 12-14px type — because these are admin
 * tools where seeing more rows at once beats generous whitespace. It reuses the
 * existing palette rather than introducing a new one: a section that recolours
 * itself reads as a different product.
 */

export const PageShell = styled.div`
  width: 100%;
  max-width: 1500px;
  margin: 0 auto;
  padding: 0 1rem 2rem 1rem;
`;

export const PageHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem 1.25rem;
  margin-bottom: 1rem;

  h1 {
    margin: 0 0 3px 0;
    font-size: 1.32rem;
    font-weight: 700;
    letter-spacing: -0.3px;
    color: ${colors.text};
  }
  p {
    margin: 0;
    font-size: 0.83rem;
    color: ${colors.textMed};
  }
`;

export const HeadActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
`;

export const Card = styled.div`
  background: ${colors.white};
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
`;

export const CardHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 13px 18px;
  border-bottom: 1px solid ${colors.inputBorderWhite};

  .count {
    font-size: 0.85rem;
    font-weight: 600;
    color: ${colors.text};
  }
  .count strong {
    color: ${colors.brown};
    font-variant-numeric: tabular-nums;
  }
`;

/** Stat tiles. Same shape as the Social Media screen so the app reads as one. */
export const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(178px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

export const Tile = styled.div`
  background: ${colors.white};
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${(props) => props.$stripe || colors.brown};
  }

  .label {
    margin: 0 0 6px 0;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: ${colors.textLight};
  }
  .figure {
    font-size: 1.7rem;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.5px;
    font-variant-numeric: tabular-nums;
    color: ${(props) => props.$figure || colors.text};
  }
  .meta {
    margin: 6px 0 0 0;
    font-size: 0.72rem;
    color: ${colors.textMed};
  }
`;

/** Status pill. Colour alone never carries the meaning — the label does. */
export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
  white-space: nowrap;
  color: ${(props) => props.$color};
  background: ${(props) => props.$bg};

  &::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
  }
`;

export const StaffStatus = {
  active: { label: "Active", color: "#1B7F3B", bg: "rgba(40, 167, 69, 0.10)" },
  blocked: { label: "Blocked", color: "#8a6400", bg: "rgba(250, 188, 11, 0.16)" },
  removed: { label: "Removed", color: colors.veryHot, bg: "rgba(232, 11, 11, 0.09)" },
};

/** Two lines in one cell: the thing, and its qualifier underneath. */
export const Stacked = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  line-height: 1.35;

  .primary {
    font-weight: 600;
    color: ${colors.text};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sub {
    font-size: 0.7rem;
    color: ${colors.textLight};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Muted = styled.span`
  color: ${colors.textLight};
`;

export const Numeric = styled.span`
  font-variant-numeric: tabular-nums;
`;

const buttonBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 9px 15px;
  min-height: 40px;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 170ms ease, border-color 170ms ease, opacity 170ms ease;

  svg {
    font-size: 1.05rem;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid #2D7AF4;
    outline-offset: 2px;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  background: ${colors.brown};
  color: ${colors.white};
  border: 1px solid ${colors.brown};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

export const GhostButton = styled.button`
  ${buttonBase}
  background: ${colors.white};
  color: ${colors.textMed};
  border: 1px solid ${colors.inputBorderWhite};

  &:hover:not(:disabled) {
    border-color: ${colors.brown};
    color: ${colors.brown};
  }
`;

/**
 * Row-level icon action. 34px is below the 44px touch minimum, which is
 * acceptable here because these rows are mouse-driven admin tables — but the
 * hit area is padded rather than relying on the icon glyph alone.
 */
export const IconAction = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 8px;
  cursor: pointer;
  background: transparent;
  border: 1px solid transparent;
  color: ${(props) => props.$tone || colors.textMed};
  transition: background 160ms ease, border-color 160ms ease;

  svg {
    font-size: 1.1rem;
  }
  &:hover:not(:disabled) {
    background: ${colors.lightWhite};
    border-color: ${colors.inputBorderWhite};
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid #2D7AF4;
    outline-offset: 1px;
  }
`;

/**
 * Shared MUI DataGrid styling.
 *
 * Spread into a DataGrid's `sx` so every Staff table has the same row height,
 * header treatment and focus ring. Rewriting these per screen is how the four
 * pages drifted apart in the first place.
 */
export const dataGridSx = {
  border: "none",
  fontSize: "0.82rem",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: colors.lightWhite,
    borderBottom: `1px solid ${colors.inputBorderWhite}`,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontSize: "0.68rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    color: colors.textLight,
  },
  "& .MuiDataGrid-cell": {
    borderBottom: `1px solid ${colors.inputBorderWhite}`,
  },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
    outline: `2px solid ${colors.brown}`,
    outlineOffset: "-2px",
  },
  "& .MuiCheckbox-root.Mui-checked": { color: colors.brown },
  "& .MuiDataGrid-row:hover": { backgroundColor: colors.lightWhite },
  // A removed employee stays visible in the list but must not read as active.
  "& .MuiDataGrid-row.row-removed": {
    opacity: 0.62,
    boxShadow: `inset 3px 0 0 0 ${colors.veryHot}`,
  },
  "& .MuiDataGrid-row.row-blocked": {
    boxShadow: `inset 3px 0 0 0 ${colors.moderate}`,
  },
};

export const GridWrap = styled.div`
  height: 64vh;
  min-height: 380px;
  position: relative;
`;

export const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  padding: 48px 24px;

  svg {
    font-size: 2.2rem;
    color: ${colors.textLight};
    margin-bottom: 10px;
  }
  h3 {
    margin: 0 0 6px 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: ${colors.text};
  }
  p {
    margin: 0;
    font-size: 0.82rem;
    color: ${colors.textMed};
    max-width: 46ch;
  }
`;
